import React from 'react';

import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import type { AiSuggestionsPlugin } from './aiSuggestionsPluginType';
import { acceptAllSuggestionsAction } from './editor-actions/accept-all-suggestions';
import { acceptSuggestionAction } from './editor-actions/accept-suggestion';
import { completeThinkingAction } from './editor-actions/complete-thinking';
import { discardAllSuggestionsAction } from './editor-actions/discard-all-suggestions';
import { discardSuggestionAction } from './editor-actions/discard-suggestion';
import { hideSuggestionDiffAction } from './editor-actions/hide-suggestion-diff';
import { refreshSuggestionsAction } from './editor-actions/refresh-suggestions';
import { selectSuggestionAction } from './editor-actions/select-suggestion';
import { setUIVisibilityAction } from './editor-actions/set-ui-visibility';
import { showSuggestionDiffAction } from './editor-actions/show-suggestion-diff';
import { startThinkingAction } from './editor-actions/start-thinking';
import { stopThinkingAction } from './editor-actions/stop-thinking';
import { aiSuggestionsPluginKey, createPlugin } from './pm-plugins/main';
import { createThinkingPlugin, thinkingPluginKey } from './pm-plugins/thinking-plugin';
import { SuggestionCardContentComponent } from './ui/components/SuggestionCard/SuggestionCardContentComponent';
import { ThinkingContentComponent } from './ui/components/Thinking/ThinkingContentComponent';
import { AISuggestionsToolbarComponents } from './ui/toolbar/toolbar';

export const aiSuggestionsPlugin: AiSuggestionsPlugin = ({ api, config }) => {
	const { contentAri, onShowSidebar, ConversationAssistant } = config;

	if (api?.toolbar) {
		api.toolbar.actions.registerComponents(AISuggestionsToolbarComponents({ api }));
	}

	return {
		name: 'aiSuggestions',
		actions: {
			acceptSuggestion: acceptSuggestionAction(api),
			discardSuggestion: discardSuggestionAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			showSuggestionDiff: showSuggestionDiffAction(api),
			// Deprecated - will remove in direct follow up - use SelectSuggestionAction instead
			hideSuggestionDiff: hideSuggestionDiffAction(api),
			acceptAllSuggestions: acceptAllSuggestionsAction(api),
			discardAllSuggestions: discardAllSuggestionsAction(api),
			refreshSuggestions: refreshSuggestionsAction(api, contentAri),
			selectSuggestion: selectSuggestionAction(api),
			setUIVisibility: setUIVisibilityAction(api),
			startThinking: startThinkingAction(api),
			completeThinking: completeThinkingAction(api),
			stopThinking: stopThinkingAction(api),
		},
		getSharedState(editorState: EditorState | undefined) {
			if (!editorState) {
				return undefined;
			}
			const pluginState = aiSuggestionsPluginKey.getState(editorState);
			if (!pluginState) {
				return undefined;
			}
			const thinkingState = thinkingPluginKey.getState(editorState);
			return {
				isHidden: pluginState.isHidden,
				selectedSuggestion: pluginState.selectedSuggestion,
				suggestionById: pluginState.suggestionById,
				baseLocalIdBySuggestionId: pluginState.baseLocalIdBySuggestionId,
				suggestionIdsByBaseLocalId: pluginState.suggestionIdsByBaseLocalId,
				orderedSuggestionIds: pluginState.orderedSuggestionIds,
				thinkingStatus: thinkingState?.status ?? 'idle',
			};
		},
		pmPlugins() {
			return [
				{
					name: 'aiSuggestionsPlugin',
					plugin: ({ getIntl, nodeViewPortalProviderAPI }) => {
						const intl = getIntl?.();
						return createPlugin({ api, intl, contentAri, nodeViewPortalProviderAPI });
					},
				},
				{
					name: 'aiSuggestionsThinkingPlugin',
					plugin: ({ dispatch }) => {
						return createThinkingPlugin({ dispatch });
					},
				},
			];
		},
		contentComponent({ editorView }) {
			return (
				<>
					{
						/**
						 * ThinkingContentComponent requires the ConversationAssistant store to
						 * invoke the skill, track thinking states, and abort requests.
						 */
						ConversationAssistant && (
							<ConversationAssistant>
								<ThinkingContentComponent api={api} />
							</ConversationAssistant>
						)
					}
					<SuggestionCardContentComponent
						api={api}
						onShowSidebar={onShowSidebar}
						view={editorView}
					/>
				</>
			);
		},
	};
};
